import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Ticket, TrendingUp, Award } from 'lucide-react';
import { useRaffle } from '../hooks/useRaffles';
import { Container } from '../components/layout/Container';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Spinner } from '../components/common/Spinner';
import { formatCurrency } from '../utils/formatters';

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

export const RaffleCountdown: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useRaffle(id!);
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({
    days: 0, hours: 0, minutes: 0, seconds: 0, total: 0,
  });

  const raffle = data?.data;
  
  const countdownTarget = raffle?.pendingDraw 
    ? raffle.pendingDraw.scheduledTime 
    : raffle?.nextDraw?.scheduledTime || raffle?.nextDrawDate;

  useEffect(() => {
    if (!countdownTarget) return;

    const calculateTimeRemaining = () => {
      const now = new Date().getTime();
      const drawTime = new Date(countdownTarget).getTime();
      const difference = drawTime - now;

      if (difference <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 });
        return;
      }

      setTimeRemaining({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
        total: difference,
      });
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);
    return () => clearInterval(interval);
  }, [countdownTarget]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
        <Container className="py-12"><Spinner size="lg" /></Container>
      </div>
    );
  }

  if (!raffle) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
        <Container className="py-12">
          <Card><p className="text-center text-gray-500 dark:text-gray-400 py-8">Raffle not found</p></Card>
        </Container>
      </div>
    );
  }

  const currentDrawTicketsSold = raffle.nextDraw?.ticketsSold || raffle.currentDrawTicketsSold || 0;
  const currentDrawSales = raffle.nextDraw?.sales || raffle.currentDrawSales || 0;
  const prizeArray = raffle.prizeDistribution?.split(',').map((p: string) => parseFloat(p.trim())) || [];
  const winnersShare = prizeArray.reduce((a: number, b: number) => a + b, 0);

  const timeUnits = [
    { value: timeRemaining.days, label: 'Days' },
    { value: timeRemaining.hours, label: 'Hours' },
    { value: timeRemaining.minutes, label: 'Minutes' },
    { value: timeRemaining.seconds, label: 'Seconds' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
      <Container className="py-10">
        <Link to={`/raffles/${id}`} className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" />
          Back to Raffle Details
        </Link>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <Card className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">{raffle.name}</h1>
            <p className="text-gray-500 dark:text-gray-400">{raffle.description}</p>
          </Card>

          {/* Countdown Timer */}
          {countdownTarget && timeRemaining.total > 0 ? (
            <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-8 text-white shadow-lg">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Clock className="h-5 w-5 text-indigo-200" />
                  <h2 className="text-xl font-bold">
                    {raffle.pendingDraw ? 'Pending Draw' : 'Next Draw In'}
                  </h2>
                </div>
                <p className="text-indigo-200 text-sm flex items-center justify-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {new Date(countdownTarget).toLocaleString()}
                </p>
                {raffle.pendingDraw && (
                  <p className="text-amber-200 mt-2 text-sm">⚠️ Draw processing delayed — results coming soon</p>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {timeUnits.map((unit) => (
                  <div key={unit.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center">
                    <div className="text-3xl sm:text-4xl md:text-5xl font-bold tabular-nums">{unit.value}</div>
                    <div className="text-xs uppercase tracking-wider text-indigo-200 mt-1">{unit.label}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-8 text-white text-center shadow-lg">
              <h2 className="text-3xl font-bold mb-2">Draw Time!</h2>
              <p className="text-amber-100">The draw is happening now or has completed. Check back soon for results.</p>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-50 dark:bg-emerald-950 rounded-full -mr-6 -mt-6" />
              <div className="relative flex items-center gap-3">
                <div className="bg-emerald-100 dark:bg-emerald-900 p-2.5 sm:p-3 rounded-xl flex-shrink-0">
                  <Ticket className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {raffle.pendingDraw ? 'Next Draw Tickets' : 'Current Draw Tickets'}
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">{currentDrawTicketsSold}</p>
                </div>
              </div>
            </Card>

            <Card className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-indigo-50 dark:bg-indigo-950 rounded-full -mr-6 -mt-6" />
              <div className="relative flex items-center gap-3">
                <div className="bg-indigo-100 dark:bg-indigo-900 p-2.5 sm:p-3 rounded-xl flex-shrink-0">
                  <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {raffle.pendingDraw ? 'Next Draw Pot' : 'Current Draw Pot'}
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 truncate">
                    {formatCurrency(currentDrawSales, raffle.currency)}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Draw Info */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <Award className="h-5 w-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Draw Information</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Number of Winners', value: raffle.numberOfWinners },
                { label: 'Ticket Price', value: formatCurrency(raffle.ticketPrice, raffle.currency) },
                { label: 'Draw Schedule', value: raffle.drawSchedule.replace('_', ' ') },
                { label: 'Winners Share', value: `${winnersShare}%` },
              ].map((item) => (
                <div key={item.label} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.label}</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-0.5">{item.value}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Buy Tickets */}
          {raffle.status === 'ACTIVE' && (
            <div className="text-center">
              <Link to={`/raffles/${id}`}>
                <Button size="lg" className="px-12">
                  <Ticket className="h-5 w-5 mr-2" />
                  Buy Tickets Now
                </Button>
              </Link>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};
